import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { routeTree } from './routeTree.gen'
import { createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import { TanStackRouterDevtools } from './core/components/TanStackRouterDevtools.tsx'
import TanStackRouterProvider from './core/components/TanStackRouterProvider.tsx'
import { Toaster } from './core/components/ui/toaster.tsx'
import IntlSelectorProvider from './core/context/IntlContext.tsx'
import { AuthProvider } from './core/context/authContext.tsx'
import { NotFoundRoot } from './core/components/NotFoundRoot.tsx'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import * as Sentry from '@sentry/react'

const queryClient = new QueryClient()

const router = createRouter({
  routeTree,
  context: { queryClient, auth: undefined! },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFoundRoot,
  notFoundMode: 'fuzzy',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export type Router = typeof router

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://fb9c067d5d68d5ae42f4a68134b48fc7@o4508676656398336.ingest.us.sentry.io/4508682047651840',
    integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],

    // Setting a sample rate is required for sending performance data.
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control.
    environment: 'development',
    tracesSampleRate: 1.0,
  })
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <IntlSelectorProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TanStackRouterProvider router={router} />
          <Suspense>
            <TanStackRouterDevtools router={router} />
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
          <Toaster />
        </AuthProvider>
      </QueryClientProvider>
    </IntlSelectorProvider>
  </React.StrictMode>
)
